/**
 * Frontend mirror of PSD pure-function calculations.
 *
 * MUST stay in sync with:
 *   - `backend/src/calculation/psdCalculations.ts`
 *   - `backend/src/calculation/hydrometerCalculations.ts`
 *   - `backend/src/calculation/uscsClassification.ts`
 *
 * Backend = source of truth on save. Frontend = live preview only.
 */
import type { PSDGeneral, PSDSieveDetail, PSDHydroDetail } from '../../../types/psd';

export interface SieveRowComputed {
  subsamplePassingPct: number | null;
  passingWholePct: number | null;
}

export interface HydroReadingComputed {
  dynamicViscosity: number | null;
  kConstant: number | null;
  modifiedReadingRm: number | null;
  effectiveDepthLcm: number | null;
  particleDiameterMm: number | null;
  percentPassingWhole: number | null;
}

const VISCOSITY_TABLE: ReadonlyArray<{ tempC: number; viscosity: number }> = [
  { tempC: 0,  viscosity: 1.7921 }, { tempC: 1,  viscosity: 1.7313 },
  { tempC: 2,  viscosity: 1.6728 }, { tempC: 3,  viscosity: 1.6191 },
  { tempC: 4,  viscosity: 1.5674 }, { tempC: 5,  viscosity: 1.5188 },
  { tempC: 6,  viscosity: 1.4728 }, { tempC: 7,  viscosity: 1.4284 },
  { tempC: 8,  viscosity: 1.3860 }, { tempC: 9,  viscosity: 1.3462 },
  { tempC: 10, viscosity: 1.3077 }, { tempC: 11, viscosity: 1.2713 },
  { tempC: 12, viscosity: 1.2363 }, { tempC: 13, viscosity: 1.2028 },
  { tempC: 14, viscosity: 1.1709 }, { tempC: 15, viscosity: 1.1404 },
  { tempC: 16, viscosity: 1.1111 }, { tempC: 17, viscosity: 1.0828 },
  { tempC: 18, viscosity: 1.0559 }, { tempC: 19, viscosity: 1.0299 },
  { tempC: 20, viscosity: 1.0050 }, { tempC: 21, viscosity: 0.9810 },
  { tempC: 22, viscosity: 0.9579 }, { tempC: 23, viscosity: 0.9358 },
  { tempC: 24, viscosity: 0.9142 }, { tempC: 25, viscosity: 0.8937 },
  { tempC: 26, viscosity: 0.8737 }, { tempC: 27, viscosity: 0.8545 },
  { tempC: 28, viscosity: 0.8360 }, { tempC: 29, viscosity: 0.8180 },
  { tempC: 30, viscosity: 0.8007 }, { tempC: 31, viscosity: 0.7840 },
  { tempC: 32, viscosity: 0.7679 }, { tempC: 33, viscosity: 0.7523 },
  { tempC: 34, viscosity: 0.7371 }, { tempC: 35, viscosity: 0.7225 },
  { tempC: 36, viscosity: 0.7085 }, { tempC: 37, viscosity: 0.6947 },
  { tempC: 38, viscosity: 0.6814 }, { tempC: 39, viscosity: 0.6685 },
  { tempC: 40, viscosity: 0.6560 },
];

export function viscosityAt(tempC: number): number | null {
  if (!Number.isFinite(tempC)) return null;
  const t = VISCOSITY_TABLE;
  if (tempC <= t[0].tempC) return t[0].viscosity;
  if (tempC >= t[t.length - 1].tempC) return t[t.length - 1].viscosity;
  for (let i = 0; i < t.length - 1; i++) {
    if (tempC >= t[i].tempC && tempC <= t[i + 1].tempC) {
      const frac = (tempC - t[i].tempC) / (t[i + 1].tempC - t[i].tempC);
      return t[i].viscosity + frac * (t[i + 1].viscosity - t[i].viscosity);
    }
  }
  return null;
}

const LOOKUP_151H_L: ReadonlyArray<{ R: number; L: number }> = [
  { R: 1.000, L: 16.3 }, { R: 1.005, L: 15.0 }, { R: 1.010, L: 13.7 },
  { R: 1.015, L: 12.5 }, { R: 1.020, L: 11.2 }, { R: 1.025, L: 9.9  },
  { R: 1.030, L: 8.6  }, { R: 1.035, L: 7.3  }, { R: 1.040, L: 6.0  },
];

function effectiveDepthL(
  hydrometerType: '151H' | '152H',
  R: number, J1: number | null, J2: number | null,
): number | null {
  if (hydrometerType === '152H') {
    if (J1 === null || J2 === null) return null;
    return J1 * R + J2;
  }
  const t = LOOKUP_151H_L;
  if (R <= t[0].R) return t[0].L;
  if (R >= t[t.length - 1].R) return t[t.length - 1].L;
  for (let i = 0; i < t.length - 1; i++) {
    if (R >= t[i].R && R <= t[i + 1].R) {
      const frac = (R - t[i].R) / (t[i + 1].R - t[i].R);
      return t[i].L + frac * (t[i + 1].L - t[i].L);
    }
  }
  return null;
}

function num(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return v;
}

/**
 * Compute % subsample passing and % passing whole for each sieve row.
 * Mirrors `qa/2ndtest/PSD_Input.xlsm` "Sieve (2)" formulas — see backend
 * `computeSieveRows` for the full algorithm.
 */
export function computeSieveRowsLive(
  rows: PSDSieveDetail[],
  general: PSDGeneral,
): SieveRowComputed[] {
  // FEAT-2026-0005 — Total dry mass now prefers the Sieve-Setup container
  // fields the new UI exposes; the legacy pan-derived calculation is kept
  // as a fallback so historical rows that only filled the pan inputs still
  // compute.
  const totalDryMass =
    general.massOfDrySoilAndContainer != null && general.massOfContainer != null
      ? general.massOfDrySoilAndContainer - general.massOfContainer
      : general.totalMassAirOvenDrySampleAndPan != null && general.massOfPan != null
      ? general.totalMassAirOvenDrySampleAndPan - general.massOfPan
      : null;
  const total = num(totalDryMass);
  const sep1 = num(general.designated1stSeparationSieveMm);
  const sep2 = num(general.designated2ndSeparationSieveMm);

  // Subsample masses (denominators for % subsample passing — Excel J46/J50).
  const sub1 = num(general.dryMassOfSubsampleBelow1st);
  const sub2 = num(general.dryMassOfSubsampleBelow2nd);

  // Whole-passing-sep masses (numerators for % whole scale — Excel J45/J49).
  // Lazily derive J45 from totalDry − retained when the persisted field is null,
  // so a stale Pre1stSepMassBelowDry doesn't poison the calc.
  const retained1 = num(general.pre1stSepDryMassRetained);
  const wholeBelow1 =
    num(general.pre1stSepMassBelowDry) ??
    (total !== null && retained1 !== null ? total - retained1 : null);
  const wholeBelow2 = num(general.pre2ndSepDryMassPassing);

  const scale2 = total && wholeBelow1 && total > 0 ? wholeBelow1 / total : 1;
  const scale3 = total && wholeBelow2 && total > 0 ? wholeBelow2 / total : 1;

  // When hydrometer is off, sub-2mm sieves are inert (matches legacy KeyLAB
  // Excel which skips those rows for non-Hydro tests).
  const hydroOff = general.hydrometerEnabled !== true;

  return rows.map((row) => {
    if (hydroOff && row.sieveSizeMm < 2) {
      return { subsamplePassingPct: null, passingWholePct: null };
    }
    const cum = num(row.cumulativeMassRetainedG);
    if (total === null || cum === null || total <= 0) {
      return { subsamplePassingPct: null, passingWholePct: null };
    }
    let tierMass: number;
    let tierScale: number;
    if (sep2 !== null && row.sieveSizeMm <= sep2) {
      tierMass = sub2 && sub2 > 0 ? sub2 : total;
      tierScale = sub2 && sub2 > 0 ? scale3 : 1;
    } else if (sep1 !== null && row.sieveSizeMm <= sep1) {
      tierMass = sub1 && sub1 > 0 ? sub1 : total;
      tierScale = sub1 && sub1 > 0 ? scale2 : 1;
    } else {
      tierMass = total;
      tierScale = 1;
    }
    if (tierMass <= 0) return { subsamplePassingPct: null, passingWholePct: null };
    const sub = (1 - cum / tierMass) * 100;
    return { subsamplePassingPct: sub, passingWholePct: sub * tierScale };
  });
}

/** Compute one hydrometer reading row. */
export function computeHydroRowLive(
  row: PSDHydroDetail,
  general: PSDGeneral,
): HydroReadingComputed {
  const R = num(row.hydrometerReadingR);
  const T = num(row.temperatureC);
  const t = num(row.elapsedTimeMin);
  const Gs = num(general.gsValue);
  if (R === null || T === null || t === null || t <= 0 || Gs === null || Gs <= 1) {
    return { dynamicViscosity: null, kConstant: null, modifiedReadingRm: null,
      effectiveDepthLcm: null, particleDiameterMm: null, percentPassingWhole: null };
  }
  const eta = viscosityAt(T);
  if (eta === null) {
    return { dynamicViscosity: null, kConstant: null, modifiedReadingRm: null,
      effectiveDepthLcm: null, particleDiameterMm: null, percentPassingWhole: null };
  }
  const etaPoise = eta / 100;
  const kConstant = Math.sqrt((30 * etaPoise) / (980 * (Gs - 1)));
  const slope = num(general.compositeCorrectionSlope) ?? 0;
  const intercept = num(general.compositeCorrectionIntercept) ?? 0;
  const Cc = slope * T + intercept;
  const Rm = R - Cc;
  const L = effectiveDepthL(
    (general.hydrometerType ?? '152H') as '151H' | '152H',
    R,
    num(general.calibrationConstantJ1),
    num(general.calibrationConstantJ2),
  );
  if (L === null || L <= 0) {
    return { dynamicViscosity: eta, kConstant, modifiedReadingRm: Rm,
      effectiveDepthLcm: null, particleDiameterMm: null, percentPassingWhole: null };
  }
  const D = kConstant * Math.sqrt(L / t);
  const a = (1.65 * Gs) / (2.65 * (Gs - 1));
  const W = num(general.totalDryMassRepresentedInHydroW);
  const P = W !== null && W > 0 ? (Rm * a * 100) / W : null;
  return {
    dynamicViscosity: eta, kConstant,
    modifiedReadingRm: Rm,
    effectiveDepthLcm: L,
    particleDiameterMm: D,
    percentPassingWhole: P,
  };
}

export interface CurvePoint { sizeMm: number; passingPct: number; }

/** Build combined sieve+hydro curve sorted descending. */
export function buildCurveLive(
  sieveRows: PSDSieveDetail[],
  hydroRows: PSDHydroDetail[],
  general: PSDGeneral,
): CurvePoint[] {
  const sieveComputed = computeSieveRowsLive(sieveRows, general);
  const hydroComputed = hydroRows.map((h) => computeHydroRowLive(h, general));
  const points: CurvePoint[] = [];
  sieveRows.forEach((s, i) => {
    const p = sieveComputed[i].passingWholePct;
    if (p !== null && Number.isFinite(s.sieveSizeMm)) {
      points.push({ sizeMm: s.sieveSizeMm, passingPct: p });
    }
  });
  hydroRows.forEach((_, i) => {
    const c = hydroComputed[i];
    if (c.particleDiameterMm !== null && c.percentPassingWhole !== null) {
      points.push({ sizeMm: c.particleDiameterMm, passingPct: c.percentPassingWhole });
    }
  });
  points.sort((a, b) => b.sizeMm - a.sizeMm);
  return points;
}

export interface DSizes { D60: number | null; D30: number | null; D10: number | null; Cu: number | null; Cc: number | null; }
export interface Fractions {
  coarserThanGravelPct: number | null; gravelPct: number | null;
  sandPct: number | null; finesPct: number | null;
  siltPct: number | null; clayPct: number | null;
}

function interpolateSizeAtPercent(curve: CurvePoint[], targetPct: number): number | null {
  if (curve.length < 2) return null;
  for (let i = 0; i < curve.length - 1; i++) {
    const a = curve[i], b = curve[i + 1];
    if (a.sizeMm <= 0 || b.sizeMm <= 0) continue;
    const lo = Math.min(a.passingPct, b.passingPct);
    const hi = Math.max(a.passingPct, b.passingPct);
    if (targetPct < lo || targetPct > hi) continue;
    if (a.passingPct === b.passingPct) return Math.sqrt(a.sizeMm * b.sizeMm);
    const logA = Math.log10(a.sizeMm), logB = Math.log10(b.sizeMm);
    const t = (targetPct - a.passingPct) / (b.passingPct - a.passingPct);
    return Math.pow(10, logA + t * (logB - logA));
  }
  return null;
}

export function computeDSizesLive(curve: CurvePoint[]): DSizes {
  const D60 = interpolateSizeAtPercent(curve, 60);
  const D30 = interpolateSizeAtPercent(curve, 30);
  const D10 = interpolateSizeAtPercent(curve, 10);
  const Cu = D60 !== null && D10 !== null && D10 > 0 ? D60 / D10 : null;
  const Cc = D60 !== null && D10 !== null && D30 !== null && D60 > 0 && D10 > 0
    ? (D30 * D30) / (D60 * D10) : null;
  return { D60, D30, D10, Cu, Cc };
}

export function computeFractionsLive(curve: CurvePoint[]): Fractions {
  const passAt = (size: number): number | null => {
    if (curve.length === 0) return null;
    const sorted = curve.slice().sort((a, b) => b.sizeMm - a.sizeMm);
    const max = sorted[0], min = sorted[sorted.length - 1];
    if (size >= max.sizeMm) return max.passingPct;
    if (size <= min.sizeMm) return min.passingPct;
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i], b = sorted[i + 1];
      if (size <= a.sizeMm && size >= b.sizeMm) {
        if (a.sizeMm === b.sizeMm) return (a.passingPct + b.passingPct) / 2;
        const logA = Math.log10(a.sizeMm), logB = Math.log10(b.sizeMm), logS = Math.log10(size);
        const t = (logS - logA) / (logB - logA);
        return a.passingPct + t * (b.passingPct - a.passingPct);
      }
    }
    return null;
  };
  const p75 = passAt(75);
  const p475 = passAt(4.75);
  const p075 = passAt(0.075);
  const p005 = passAt(0.005);
  return {
    coarserThanGravelPct: p75 !== null ? Math.max(0, 100 - p75) : null,
    gravelPct: p75 !== null && p475 !== null ? Math.max(0, p75 - p475) : null,
    sandPct: p475 !== null && p075 !== null ? Math.max(0, p475 - p075) : null,
    finesPct: p075,
    siltPct: p075 !== null && p005 !== null ? Math.max(0, p075 - p005) : null,
    clayPct: p005,
  };
}
